// The animated post-processing values of the original's `PostProcessor` (PostProcessor.cpp,
// PP_Effect.cpp): the effect-mode animators (sepia/grey, tint, invert, vivid) and the vignette
// that `EffectsPass` (`PP_SepiaF`, passes.ts) reads, and the named effects that start them
// (`CreateAndPlayNamedEffect` 0xcfed40 / `EffectData::PlayEffect` 0xd04890). The clock is the
// presented frame: `PostProcessor::Update` takes the frame time, `PP_Effect::Animate` turns the
// delta into 60 Hz frames, and the speeds are per frame.
//
// Driven by `TurnLogic::ApplySuddenDeathEffect` 0xd5a780 (the `CAM_SUDDEN_DEATH` view event: the
// named effect `SuddenDeathEffect` of GameOptions `PostProcess`, `Tint_Defaults` with its
// `TintDefaults.Colour` and `Vignette_Defaults` with its `VignetteDefault`) and by
// `CreateAndPlayExplosionEffect` (`CAM_POST_EFFECT`). The Bloom type drives `PP_Bloom`
// (`BloomFlash` 0xcfd600, `PP_Bloom::Animate` 0xd4bbd0: blur size, intensity and threshold of
// the bloom passes between the theme's `BloomDefaults` and the flash's values). The Vignette
// effect type (`Vignette_In`/`Vignette_Flash`) is not ported: no shipped named effect uses it.
import { assetJson } from '../assets.ts';

/** `DAT_015bc8c8`: the animators' "reached" epsilon (1/255). */
const EPS = 1 / 255;
/** `CreateAndPlayNamedEffect`'s slot count (`m_ExplosionEffect[5]`). */
const EFFECT_SLOTS = 5;

/** `PP_Effect` (PP_Effect.cpp). */
class Animator {
  flash = false;
  sustain = false;
  sustaining = false;
  first = true;
  speedIn = 0;
  speedOut = 0;
  current = 0;
  dest = 0;
  start = 0;
  sustainSpeed = 0;
  startSustain = 0;
  currentSustain = 0;
  sustainDest = 0;

  /** `PP_Effect::Animate(RelativeTime_t)` 0xd4b8e0; `dt` in seconds. (Its `m_bChanged` only
   * decides whether the uniforms are rewritten; `PostEffects.update` derives them every
   * frame.) */
  animate(dt: number): void {
    let cur = this.current;
    const dest = this.dest;
    if (cur === dest && !this.first) return;
    this.first = false;
    const frames = Math.fround(dt / Math.fround(1 / 60));
    if (!this.sustaining) {
      const step = Math.fround(frames * (dest < cur ? this.speedOut : this.speedIn));
      const start = this.start;
      if (dest <= start) {
        let v = Math.fround(cur - step);
        if (start <= v) v = start;
        cur = dest <= v ? v : dest;
      } else {
        let v = Math.fround(step + cur);
        if (dest <= v) v = dest;
        cur = start <= v ? v : start;
      }
      this.current = cur;
    } else {
      const step = Math.fround(frames * this.sustainSpeed);
      const startS = this.startSustain, destS = this.sustainDest;
      let s: number;
      if (destS <= startS) {
        let v = Math.fround(this.currentSustain - step);
        if (startS <= v) v = startS;
        s = destS <= v ? v : destS;
      } else {
        let v = Math.fround(step + this.currentSustain);
        if (destS <= v) v = destS;
        s = startS <= v ? v : startS;
      }
      this.currentSustain = s;
      if (Math.abs(s - destS) < EPS) {
        this.sustaining = false;
        this.sustain = false;
      }
    }
    if (Math.abs(cur - dest) < EPS) {
      if (this.flash) {
        this.current = dest;
        this.start = dest;
        this.dest = 0;
        cur = dest;
      }
      if (cur === 0) {
        this.flash = false;
        return;
      }
      if (this.sustain && !this.sustaining) this.sustaining = true;
    }
  }

  /** The flash setup shared by `Sepia_Flash`, `Tint_Flash`, `Vivid_Flash`, `Invert_Flash`:
   * in, sustain and out speeds, 0 → intensity, then back to 0 after the sustain. */
  startFlash(inSpeed: number, sustainSpeed: number, outSpeed: number, intensity: number, sustainDest: number): void {
    this.speedIn = inSpeed;
    this.speedOut = outSpeed;
    this.flash = true;
    this.dest = intensity;
    this.start = 0;
    this.current = 0;
    this.sustain = sustainSpeed !== 0;
    this.sustaining = false;
    this.sustainSpeed = sustainSpeed;
    this.startSustain = 0;
    this.currentSustain = 0;
    this.sustainDest = sustainSpeed !== 0 ? sustainDest : 0;
  }
}

type Colour = [number, number, number, number];

/** GameOptions `PostProcess.<name>N` (`EffectData::CreateEffectFromDatum` 0xcfea60). */
type EffectData = {
  type: string; style: string; parallel: boolean;
  inSpeed: number; outSpeed: number; sustainSpeed: number; intensity: number; colour: Colour;
  /** `m_fBlurSize`, `m_fIntensity`, `m_fThreshold` (the Bloom type's). */
  blurSize: number; bloomIntensity: number; threshold: number;
};

type DatumNode = { name?: string; value?: string; children?: DatumNode[] };

function child(n: DatumNode | undefined, name: string): DatumNode | undefined {
  return n?.children?.find(c => c.name?.toLowerCase() === name.toLowerCase());
}

function num(n: DatumNode | undefined): number {
  const v = Number(n?.value);
  return Number.isFinite(v) ? v : 0;
}

function str(n: DatumNode | undefined): string {
  return (n?.value ?? '').replace(/^"|"$/g, '');
}

/** `SetInDuration` / `SetOutDuration` / `SetSustainDuration`: intensity / duration / 60 clamped
 * to 0..1 (a zero duration leaves the speed at 0). */
function speed(intensity: number, duration: number): number {
  if (duration === 0) return 0;
  const v = Math.fround(Math.fround(intensity / duration) / 60);
  return v < 1 ? (v >= 0 ? v : 0) : 1;
}

function effectData(d: DatumNode): EffectData {
  const raw = num(child(d, 'EffectIntensity'));
  const intensity = raw < 1 ? (raw >= 0 ? raw : 0) : 1;
  const c = child(d, 'Colour');
  return {
    type: str(child(d, 'Type')), style: str(child(d, 'Style')), parallel: str(child(d, 'PlaysParallel')) === 'true',
    inSpeed: speed(intensity, num(child(d, 'InDuration'))), outSpeed: speed(intensity, num(child(d, 'OutDuration'))),
    sustainSpeed: speed(intensity, num(child(d, 'SustainDuration'))), intensity,
    // CreateEffectFromDatum's colour for Sepia and Tint: (r, g, b) with w 0.
    colour: [num(child(c, 'r')), num(child(c, 'g')), num(child(c, 'b')), 0],
    blurSize: num(child(d, 'BlurSize')), bloomIntensity: num(child(d, 'Intensity')), threshold: num(child(d, 'Threshold')),
  };
}

/** An effect of the sim's events (`sim::view::camera::effect_values` without colour a: type 0
 * Bloom .. 6 Vignette, style 0 Flash / 1 Manual, parallel, in/out/sustain duration, effect
 * intensity, blur size, bloom intensity, threshold, colour r, g, b) as `CreateEffectFromDatum`
 * 0xcfea60 builds it: the intensity clamped to 0..1, the speeds from it, colour w 0. */
function effectFromArgs(a: number[]): EffectData {
  const types = ['Bloom', 'Tint', 'Sepia', 'GreyScale', 'Vivid', 'Invert', 'Vignette'];
  const raw = a[6];
  const intensity = raw < 1 ? (raw >= 0 ? raw : 0) : 1;
  return {
    type: types[a[0]] ?? '', style: ['Flash', 'Manual'][a[1]] ?? '', parallel: a[2] !== 0,
    inSpeed: speed(intensity, a[3]), outSpeed: speed(intensity, a[4]), sustainSpeed: speed(intensity, a[5]), intensity,
    colour: [a[10], a[11], a[12], 0], blurSize: a[7], bloomIntensity: a[8], threshold: a[9],
  };
}

/** `LaserStrikeGadget`'s effect data (`m_IdleEffects`, `m_ChargeEffects`, `m_FireEffects`, five
 * slots each, a missing one not created) and its graphical pulse members (`m_tIdlePulseTime`,
 * `m_tFirePulseTime`, `m_tLastIdlePulse`, `m_tLastFirePulse`). */
type LaserEffects = { groups: (EffectData | null)[][]; idlePulse: number; firePulse: number; lastIdle: number; lastFire: number };

/** GameOptions `PostProcess` as `TurnLogic::Initialize` 0xd5b700 reads it. */
type SuddenDeath = { vignette: number; tint: Colour; tintIntensity: number; effects: EffectData[] };

let suddenDeathData: Promise<SuddenDeath> | null = null;

function loadSuddenDeath(): Promise<SuddenDeath> {
  suddenDeathData ??= assetJson<{ root: DatumNode }>('data/scripts/json/CommonData/Scripts/GameOptions.txt.json').then(doc => {
    const pp = child(doc.root, 'PostProcess');
    const c = child(child(pp, 'TintDefaults'), 'Colour');
    // CreateAndPlayNamedEffect: SuddenDeathEffect1, 2, ... until one is missing, at most 5.
    const effects: EffectData[] = [];
    for (let i = 1; i <= EFFECT_SLOTS; i++) {
      const d = child(pp, `SuddenDeathEffect${i}`);
      if (!d) break;
      effects.push(effectData(d));
    }
    return { vignette: num(child(pp, 'VignetteDefault')), tint: [num(child(c, 'r')), num(child(c, 'g')), num(child(c, 'b')), 1], tintIntensity: num(child(c, 'a')), effects };
  });
  return suddenDeathData;
}

/** The uniforms of `EffectsPass` (`PP_SepiaF`) and of the bloom passes (`PP_Bloom::Animate`'s
 * blur size, `BloomLumMulti`, `BloomLumThreshold`). */
export type EffectUniforms = {
  effectMode: Colour; sepiaColour: Colour; tintColour: Colour; vignette: number;
  bloomBlurSize: number; bloomIntensity: number; bloomThreshold: number;
};

export class PostEffects {
  private sepia = new Animator();
  /** `PP_Sepia`'s colour (`PP_Sepia::Reset`: (1, 0.7, 0.4, 1)). */
  private sepiaColour: Colour = [1, 0.7, 0.4, 1];
  private vivid = new Animator();
  private invert = new Animator();
  private tint = new Animator();
  private tintFrom: Colour = [0, 0, 0, 0];
  private tintTo: Colour = [0, 0, 0, 0];
  private tintIntensityFrom = 0;
  private tintIntensityTo = 0;
  private vignette = new Animator();
  /** `PP_Vignette`'s from (`field_0x34`) and to (`m_fVignetteTo`): the theme's
   * `VignetteIntensity` (`ThemeMan::LoadResources` → `Vignette_Defaults`). */
  private vignetteFrom: number;
  private vignetteTo: number;
  /** `PP_Bloom`: the animator and its from (the theme's `BloomDefaults`) and to values. */
  private bloom = new Animator();
  private bloomFrom = { blurSize: 0, intensity: 0, threshold: 0 };
  private bloomTo = { blurSize: 0, intensity: 0, threshold: 0 };
  /** `m_ExplosionEffect`: the created named effects waiting for `__UpdateEffects`. */
  private slots: (EffectData | null)[] = new Array<EffectData | null>(EFFECT_SLOTS).fill(null);
  private laser: LaserEffects = { groups: [[], [], []], idlePulse: 0, firePulse: 0, lastIdle: 0, lastFire: 0 };
  private lastTick = -1;
  readonly uniforms: EffectUniforms = {
    effectMode: [0, 0, 0, 0], sepiaColour: [1, 0.7, 0.4, 1], tintColour: [0, 0, 0, 0], vignette: 0,
    bloomBlurSize: 0, bloomIntensity: 0, bloomThreshold: 0,
  };

  constructor(themeVignette: number) {
    this.vignetteFrom = this.vignetteTo = themeVignette;
  }

  /** `Vignette_Defaults` of the theme's intensity (once the theme has loaded). */
  setThemeVignette(v: number): void {
    this.vignetteFrom = this.vignetteTo = v;
  }

  /** `PostProcessor::BloomDefaults` 0xcfd6c0 (the theme's `BloomDefaults`): from = to, the
   * animator back at 0. */
  setThemeBloom(blurSize: number, intensity: number, threshold: number): void {
    this.bloomFrom = { blurSize, intensity, threshold };
    this.bloomTo = { blurSize, intensity, threshold };
    Object.assign(this.bloom, { start: 0, current: 0, dest: 0 });
  }

  /** `TurnLogic::ApplySuddenDeathEffect` 0xd5a780: the named effect, then `Tint_Defaults` and
   * `Vignette_Defaults` (the shake is the camera's `CAM_SHAKE`). */
  async suddenDeath(): Promise<void> {
    const sd = await loadSuddenDeath();
    // CreateAndPlayNamedEffect fills the slots from the first; __UpdateEffects plays them.
    sd.effects.forEach((e, i) => { this.slots[i] = e; });
    // Tint_Defaults 0xcfd290: from = to = the intensity, both colours the given one.
    this.tintIntensityFrom = this.tintIntensityTo = sd.tintIntensity;
    this.tintFrom = [...sd.tint];
    this.tintTo = [...sd.tint];
    // Vignette_Defaults 0xcfd5d0.
    this.vignetteFrom = this.vignetteTo = sd.vignette;
  }

  /** One effect of `PostProcessor::CreateAndPlayExplosionEffect` 0xcfeef0 (the sim's
   * `CAM_POST_EFFECT`: slot, type, style, parallel, in/out/sustain duration, effect intensity,
   * blur size, bloom intensity, threshold, colour r, g, b, a): `CreateAndPlayNamedEffect` stores
   * it in its slot as `CreateEffectFromDatum` builds it; `__UpdateEffects` plays it. */
  explosion(a: number[]): void {
    this.slots[a[0]] = effectFromArgs(a.slice(1));
  }

  /** The laser strike's own post-processing (the sim's `CAM_GADGET_EFFECT`; argument 0 the
   * operation): 0 `Select` 0xb71bf0 (`m_tLastIdlePulse` = `m_tLastFirePulse` = 0, the pulse
   * times, the three `LoadEffect` 0xcfef40 calls empty every slot), 1-3 a loaded idle, charge or
   * fire effect (slot, then the effect), 4 `Update_LaserWaiting`'s idle pulse at the gadget's
   * `m_CurrentTime`, 5 the charge effects played, 6 stopped (`Update_LaserCharging`), 7
   * `Update_LaserFiring`'s fire pulse. A pulse plays the group's created effects in slot order
   * when `m_tLast + m_tPulse < m_CurrentTime` (float32) and takes the time. `PlayEffect` and
   * `StopEffect` act at once, not through `__UpdateEffects`' slots. */
  gadget(a: number[]): void {
    const L = this.laser, [idle, charge, fire] = L.groups;
    const pulse = (group: (EffectData | null)[], last: number, period: number): boolean => {
      if (!(Math.fround(last + period) < a[1])) return false;
      for (const e of group) if (e) this.play(e);
      return true;
    };
    switch (a[0]) {
      case 0: Object.assign(L, { groups: [[], [], []], idlePulse: Math.fround(a[1]), firePulse: Math.fround(a[2]), lastIdle: 0, lastFire: 0 }); break;
      case 1: case 2: case 3: L.groups[a[0] - 1][a[1]] = effectFromArgs(a.slice(2)); break;
      case 4: if (pulse(idle, L.lastIdle, L.idlePulse)) L.lastIdle = a[1]; break;
      case 5: for (const e of charge) if (e) this.play(e); break;
      case 6: for (const e of charge) if (e) this.stop(e); break;
      case 7: if (pulse(fire, L.lastFire, L.firePulse)) L.lastFire = a[1]; break;
    }
  }

  /** `EffectData::StopEffect` 0xd04db0: `Sepia_Out` / `Gray_Out` / `Tint_Out` / `Invert_Out` /
   * `Vivid_Out` / `Vignette_Out` at the out speed (dest 0; all but vivid and vignette end a
   * flash); nothing for Bloom. */
  private stop(e: EffectData): void {
    const out = { speedIn: e.outSpeed, speedOut: e.outSpeed, dest: 0 };
    switch (e.type) {
      case 'Sepia': case 'GreyScale': Object.assign(this.sepia, out, { flash: false }); break;
      case 'Tint': Object.assign(this.tint, out, { flash: false }); break;
      case 'Invert': Object.assign(this.invert, out, { flash: false }); break;
      case 'Vivid': Object.assign(this.vivid, out); break;
      case 'Vignette': Object.assign(this.vignette, out); break;
    }
  }

  /** `EffectData::PlayEffect` 0xd04890. */
  private play(e: EffectData): void {
    const flash = e.style === 'Flash', manual = e.style === 'Manual';
    if (!flash && !manual) return;
    switch (e.type) {
      case 'Bloom':
        // BloomFlash 0xcfd600 (only the Flash style): the to values, 0 → effect intensity, the
        // sustain to 1. Manual plays nothing.
        if (!flash) break;
        this.bloomTo = { blurSize: e.blurSize, intensity: e.bloomIntensity, threshold: e.threshold };
        this.bloom.startFlash(e.inSpeed, e.sustainSpeed, e.outSpeed, e.intensity, 1);
        break;
      case 'Sepia':
      case 'GreyScale': {
        // Sepia_Flash / Sepia_In with no colour: (1, 0.7, 0.4, 0); the grey variants white.
        this.sepiaColour = e.type === 'Sepia' ? [1, 0.7, 0.4, 0] : [1, 1, 1, 0];
        if (flash) this.sepia.startFlash(e.inSpeed, e.sustainSpeed, e.outSpeed, e.intensity, 1);
        else Object.assign(this.sepia, { speedIn: e.inSpeed, dest: e.intensity, start: 0, current: 0, flash: false });
        break;
      }
      case 'Tint':
        // Tint_Flash / Tint_In: the colour to, the intensity to; the flash sustains to the
        // intensity (not to 1 like the others).
        this.tintTo = [...e.colour];
        this.tintIntensityTo = e.intensity;
        if (flash) this.tint.startFlash(e.inSpeed, e.sustainSpeed, e.outSpeed, e.intensity, e.intensity);
        else Object.assign(this.tint, { speedIn: e.inSpeed, speedOut: e.inSpeed, dest: e.intensity, start: 0, current: 0, flash: false });
        break;
      case 'Vivid':
        if (flash) this.vivid.startFlash(e.inSpeed, e.sustainSpeed, e.outSpeed, e.intensity, 1);
        else Object.assign(this.vivid, { speedIn: e.inSpeed, dest: e.intensity, start: 0, current: 0 });
        break;
      case 'Invert':
        if (flash) this.invert.startFlash(e.inSpeed, e.sustainSpeed, e.outSpeed, e.intensity, 1);
        else Object.assign(this.invert, { speedIn: e.inSpeed, speedOut: e.inSpeed, dest: e.intensity, start: 0, current: 0, flash: false });
        break;
      default:
        console.warn('post effect type not ported:', e.type);
    }
  }

  /** `PostProcessor::Update` → `__UpdateEffects` 0xd060e0 at presented frame `tick`. */
  update(tick: number): void {
    const dt = this.lastTick < 0 ? 0 : Math.max(0, tick - this.lastTick) / 60;
    this.lastTick = tick;
    // __UpdateEffects: in slot order a parallel effect plays at once; another plays only while
    // no flash animator is running, and then the slot walk ends for this frame.
    for (let i = 0; i < EFFECT_SLOTS; i++) {
      const e = this.slots[i];
      if (!e) continue;
      if (!e.parallel && ([this.bloom, this.vivid, this.sepia, this.invert].some(a => a.flash && a.current > EPS) || (this.tint.flash && this.tintCurrent() > EPS))) continue;
      this.slots[i] = null;
      this.play(e);
      if (!e.parallel) break;
    }
    this.bloom.animate(dt);
    this.vivid.animate(dt);
    this.sepia.animate(dt);
    this.invert.animate(dt);
    this.tint.animate(dt);
    this.vignette.animate(dt);
    this.refreshUniforms();
  }

  /** The animators' current values into the pass uniforms (the `Animate`s' uniform writes;
   * also after a theme's defaults changed). */
  refreshUniforms(): void {
    const u = this.uniforms;
    // PP_Sepia::Animate: the colour; PP_Tint::Animate: from + (to - from) × current, w the
    // current intensity; PP_Vignette::Animate: from + (to - from) × current.
    u.sepiaColour = [...this.sepiaColour];
    const t = this.tint.current;
    u.tintColour = [0, 1, 2].map(i => t * (this.tintTo[i] - this.tintFrom[i]) + this.tintFrom[i]).concat(this.tintCurrent()) as Colour;
    u.vignette = this.vignetteFrom + (this.vignetteTo - this.vignetteFrom) * this.vignette.current;
    u.effectMode = [this.sepia.current, this.tintCurrent(), this.invert.current, this.vivid.current];
    // PP_Bloom::Animate: blur from + (to - from) × current; intensity, threshold (to - from) ×
    // current + from.
    const b = this.bloom.current, bf = this.bloomFrom, bt = this.bloomTo;
    u.bloomBlurSize = Math.fround(bf.blurSize + Math.fround(Math.fround(bt.blurSize - bf.blurSize) * b));
    u.bloomIntensity = Math.fround(Math.fround(Math.fround(bt.intensity - bf.intensity) * b) + bf.intensity);
    u.bloomThreshold = Math.fround(Math.fround(b * Math.fround(bt.threshold - bf.threshold)) + bf.threshold);
  }

  /** `PP_Tint::GetCurrent` 0xd4ba70. */
  private tintCurrent(): number {
    return (this.tintIntensityTo - this.tintIntensityFrom) * this.tint.current + this.tintIntensityFrom;
  }
}
